import { KA_FLOATING_RIVE } from '@flashka/shared/rive/ka-floating-rive';
import {
  KA_IDLE_PLAYS,
  PROFESSOR_KA_LABEL,
  professorKaTileMetrics,
} from '@flashka/shared/ui';

import { useReducedMotion } from 'framer-motion';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { cn } from '@/lib/utils';

import { KA_FLOATING_CANVAS_BOX, kaFloatingDrop } from './ka-floating-frame';

/** The Rive runtime and its wasm load lazily, never in an entry chunk. */
const KaFloatingRive = lazy(() => import('./ka-floating-rive'));

/** How long Ka stays alive per wake before he rests — the same rule as the
 *  app's floating tile and the Lottie it replaced (QA #35, battery). */
const KA_FLOATING_AWAKE_MS = KA_IDLE_PLAYS * KA_FLOATING_RIVE.cycleMs;

/** Before pausing, the gaze is handed back ahead for this long, so a resting
 *  Ka never freezes looking at a stale spot. */
const SETTLE_MS = 400;

type Phase = 'awake' | 'settling' | 'asleep';

/**
 * Prof. Ka in the app as the floating Rive (`ka-floating.riv`): idle
 * breathing and blinks, now and then writing behind his paper or peeking out
 * past his card, his eyes on the cursor — the Ka the landing shows and the
 * app's floating tile draws (`flashka-app` `KaFloatingRive`, same contract).
 *
 * `size` is the CARD's edge in px; the canvas is twice that and overflows the
 * box on every side (that margin is where his head goes when he peeks), so
 * the host must not clip it. Until the file has drawn, the box shows the bare
 * card (cream, amber ring) so it never stands empty.
 *
 * He is alive for {@link KA_FLOATING_AWAKE_MS} after mount, then rests; a
 * pointer over him wakes him for another round. Reduce Motion keeps him still.
 */
export function KaFloatingMascot({
  size,
  className,
}: {
  size: number;
  className?: string;
}) {
  const reduceMotion = !!useReducedMotion();
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState<Phase>('awake');

  useEffect(() => {
    if (reduceMotion) return;
    if (phase === 'awake') {
      const rest = window.setTimeout(() => setPhase('settling'), KA_FLOATING_AWAKE_MS);
      return () => window.clearTimeout(rest);
    }
    if (phase === 'settling') {
      const pause = window.setTimeout(() => setPhase('asleep'), SETTLE_MS);
      return () => window.clearTimeout(pause);
    }
  }, [phase, reduceMotion]);

  const wake = useCallback(() => setPhase('awake'), []);
  const onReady = useCallback(() => setReady(true), []);

  // The tile's own geometry, so the placeholder card matches the DS tile.
  const tile = professorKaTileMetrics(1, size);

  return (
    <span
      role="img"
      aria-label={PROFESSOR_KA_LABEL}
      onPointerEnter={wake}
      className={cn('relative inline-block flex-none', className)}
      style={{ width: size, height: size }}>
      {!ready && (
        <span
          aria-hidden
          className="absolute inset-0 border-solid border-amber-400 bg-amber-onFill"
          style={{ borderRadius: tile.radius, borderWidth: tile.border }}
        />
      )}
      <span
        aria-hidden
        className="pointer-events-none absolute transition-opacity duration-300"
        style={{
          ...KA_FLOATING_CANVAS_BOX,
          filter: kaFloatingDrop(size),
          opacity: ready ? 1 : 0,
        }}>
        {/* Ka is decoration: a chunk that fails to load (a flaky line, a tab
            open across a deploy) leaves the bare card, never the page. */}
        <ErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <KaFloatingRive
              followCursor={!reduceMotion && phase === 'awake'}
              still={reduceMotion || phase === 'asleep'}
              onReady={onReady}
              className="h-full w-full"
            />
          </Suspense>
        </ErrorBoundary>
      </span>
    </span>
  );
}
